import FullScreenLoader from "../Common/FullScreenLoader";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import { fetchRoomRestriction } from "../../../Redux/Slices/BON-Frontend/roomRestrictionSlice";

const materialIcon = (name, className = "") => (
    <span className={`material-symbols-outlined ${className}`} aria-hidden="true">{name}</span>
);

const hasImage = (src) => typeof src === "string" && src.trim() !== "" && src !== "N/A";

export const BookingForm = ({ pid, propertyID }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [guestSelection, setGuestSelection] = useState({});
    const {
        preBookingDetails,
        roomDetails,
        table3Details,
        checkInDate,
        checkOutDate,
        loading,
    } = useSelector(
        (state) => state.preBooking
    );
    const roomRestrictions = useSelector(
        (state) => state.frontRoomRestriction.byRequestKey
    );

    const preBookingToken = preBookingDetails?.[0]?.PreBookingToken || "";
    const bookingConfiguration = table3Details?.[0] || {};

    const getMealPlans = (mealPlanList) => {
        try {
            return mealPlanList ? JSON.parse(mealPlanList) : [];
        } catch (error) {
            console.log("MealPlan Parse Error:", error);
            return [];
        }
    };

    // RoomRate string ko parse karke rate-object nikalta hai
    // const getRateObject = (roomRateRaw) => {
    //     try {
    //         const parsed = typeof roomRateRaw === "string" ? JSON.parse(roomRateRaw) : roomRateRaw;
    //         return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : null;
    //     } catch (error) {
    //         console.log("RoomRate Parse Error:", error);
    //         return null;
    //     }
    // };

    const getRateObject = (roomRateRaw) => {
    try {
        const parsed = typeof roomRateRaw === "string" ? JSON.parse(roomRateRaw) : roomRateRaw;

        if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[0];
        }

        // Agar array nahi hai (jaise sirf "0" ya koi number aaya), 
        // toh empty rate object bana do taaki price 0 treat ho aur button disable na ho
        return {};
    } catch (error) {
        console.log("RoomRate Parse Error:", error);
        return {};
    }
};  

    // Adults/Child ke hisaab se price calculate karta hai (room count price ko affect nahi karega)
    const calculatePrice = (rateObj, adults = 1, child = 0) => {
        if (!rateObj) return 0;

        const baseRate = Number(rateObj.BaseRate) || 0;
        const doubleRate = Number(rateObj.DoubleRate) || 0;
        const tripleRate = Number(rateObj.TripleRate) || 0;
        const extraAdultRate = Number(rateObj.ExtraAdultRate) || 0;
        const extraChildRate = Number(rateObj.ExtraChildRate) || 0;

        let price;
        if (adults <= 1) {
            price = baseRate;
        } else if (adults === 2) {
            price = doubleRate;
        } else if (adults === 3) {
            price = tripleRate;
        } else {
            price = tripleRate + (adults - 3) * extraAdultRate;
        }

        price += child * extraChildRate;

        return price;
    };

    const handleGuestChange = (selectionKey, field, value) => {
        setGuestSelection((prev) => ({
            ...prev,
            [selectionKey]: {
                room: prev[selectionKey]?.room || 1,
                adults: prev[selectionKey]?.adults || 1,
                child: prev[selectionKey]?.child || 0,
                [field]: Number(value),
            },
        }));
    };

    const handleBookNow = async (room, plan, finalPrice) => {
        const selectionKey = `${room.RoomCode}_${plan.Code}`;

        try {
            const { restricted } = await dispatch(fetchRoomRestriction({
                requestKey: selectionKey,
                PropertyID: pid,
                CheckInDate: checkInDate,
                CheckOutDate: checkOutDate,
                RateCode: bookingConfiguration.RateCode || "",
                RoomCode: room.RoomCode,
            })).unwrap();

            if (restricted) {
                swal("No Room Available for This Date", {
                    icon: "warning",
                });
                return;
            }
        } catch (error) {
            swal("Unable to check room availability. Please try again.", {
                icon: "error",
            });
            return;
        }

        const roomData = [{
            roomID: room.RoomID,
            roomCode: room.RoomCode,
            roomName: room.RoomName,
            mealPlanCode: plan.Code,
            mealPlanID: plan.MealPlanID,
            mealPlanName: plan.Description,
            ratePlanCode: bookingConfiguration.RateCode || "",
            roomCount: guestSelection[selectionKey]?.room || 1,
            adults: guestSelection[selectionKey]?.adults || 1,
            child: guestSelection[selectionKey]?.child || 0,
            rate: finalPrice,
        }];

        navigate("/Booking", {
            state: {
                pid,
                PropertyID: propertyID,
                checkInDate,
                checkOutDate,
                rooms: roomData,
                preBookingToken,
                Table3: table3Details,
            },
        });
    };

    return (
        <>
            {loading && (
                <FullScreenLoader />
            )}

            {roomDetails?.length > 0 ? roomDetails.map((room, index) => {
                const mealPlans = getMealPlans(room.MealPlanList);
                const isSoldOut = Number(room.IsAvailable) === 0;

                return (
                    <div key={room.RoomID || index} className="container bp-booking-card-wrap">
                        <article className="bp-booking-card">
                            <div className="bp-room-overview">
                                <section className="bp-booking-image-wrap">
                                    <span className="bp-image-placeholder" aria-hidden="true">
                                        {materialIcon("image")}
                                    </span>
                                    {hasImage(room.RoomPhotoURL) && (
                                        <img
                                            src={room.RoomPhotoURL}
                                            alt={room.RoomName || "Room"}
                                            className="bp-booking-image"
                                            onError={(event) => {
                                                event.currentTarget.style.display = "none";
                                            }}
                                        />
                                    )}
                                    <span className="bp-image-badge">
                                        {materialIcon("hotel", "bp-badge-icon")}
                                        {room.RoomName}
                                    </span>
                                </section>

                                <section className="bp-booking-details">
                                    <h2>{room.RoomName}</h2>
                                    <p style={{ fontSize: "13px" }}>{room.Description}</p>
                                </section>
                            </div>

                            <div className="bp-plan-list">
                                {mealPlans.length > 0 ? mealPlans.map((plan, idx) => {
                                    const selectionKey = `${room.RoomCode}_${plan.Code}`;
                                    const restrictionLoading = roomRestrictions[selectionKey]?.loading;
                                    const selection = guestSelection[selectionKey] || {};
                                    const adults = selection.adults || 1;
                                    const child = selection.child || 0;

                                    const rateObj = getRateObject(plan.RoomRate);
                                    const finalPrice = calculatePrice(rateObj, adults, child);
                                    const isPlanSoldOut = isSoldOut || Number(plan.RoomRate) === 0;

                                    return (
                                        <div key={plan.MealPlanID || idx} className="bp-plan-row">
                                            <div className="bp-plan-name">
                                                <h3>{plan.Code || plan.Description}</h3>
                                                <span>{materialIcon("person", "bp-person-icon")} {adults} Guest{adults > 1 ? "s" : ""}</span>
                                            </div>

                                            <div className="bp-plan-selects">
                                                <label>
                                                    <span>Room</span>
                                                    <select disabled={isPlanSoldOut} value={selection.room || 1} onChange={(e) => handleGuestChange(selectionKey, "room", e.target.value)}>
                                                        {[...Array(2)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                                                    </select>
                                                </label>
                                                <label>
                                                    <span>Adults</span>
                                                    <select disabled={isPlanSoldOut} value={selection.adults || 1} onChange={(e) => handleGuestChange(selectionKey, "adults", e.target.value)}>
                                                        {[...Array(10)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                                                    </select>
                                                </label>
                                                <label>
                                                    <span>Child</span>
                                                    <select disabled={isPlanSoldOut} value={selection.child || 0} onChange={(e) => handleGuestChange(selectionKey, "child", e.target.value)}>
                                                        {[...Array(11)].map((_, i) => <option key={i} value={i}>{i}</option>)}
                                                    </select>
                                                </label>
                                            </div>

                                            {isPlanSoldOut ? (
                                                <div className="bp-plan-sold-out" role="status">
                                                    Sold Out
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="bp-plan-price">
                                                        <strong>
                                                            INR {finalPrice.toLocaleString("en-IN")} {materialIcon("chevron_right")}
                                                        </strong>
                                                        <span>RATE FOR 1 NIGHT</span>
                                                        <small>Tax Exclusive</small>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="bp-plan-book"
                                                        onClick={() => handleBookNow(room, plan, finalPrice)}
                                                        disabled={!rateObj || restrictionLoading}
                                                    >
                                                        {restrictionLoading ? "Checking..." : "Book"}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    );
                                }) : <div className="text-center text-muted py-4">No Meal Plans Available</div>}
                            </div>
                        </article>
                    </div>
                );
            }) : (
                <div className="container py-5">
                    <div className="card border-0 shadow-sm text-center mx-auto" style={{ maxWidth: 500 }}>
                        <div className="card-body py-5">
                            {materialIcon("bedroom_parent", "bp-empty-icon")}
                            <h3 className="mt-3 fw-bold text-dark">No Rooms Available</h3>
                            <p className="text-muted mb-4">Sorry! No rooms are available for the selected dates. Please modify your search.</p>
                            <button className="btn btn-primary px-4" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Modify Search</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
